import type {
  ChunkProcessingResult,
  CompleteMicroSegmentationResult,
  MicroUnit
} from '../types/segmentationTypes'
import {
  HIDDEN_EDITORIAL_MARKER_REGEX_SOURCE,
  LO_MARKER_REGEX_SOURCE,
  shouldHideMarkerInWorkspace
} from './workspaceMarkerVisibility'
import {
  assignStableUnitIdsByOverlap,
  extractIdentityUnitsFromSegmentationResult
} from './manualUnitIdentity'

export interface WordSpan {
  word: string
  start: number
  end: number
}

export interface FoliationSpan {
  marker: string
  start: number
  end: number
  startWordIndex: number
  endWordIndex: number
  showInUi: boolean
}

export interface SegmentableWordData {
  segmentableWordSpans: WordSpan[]
  originalToSegmentableWordIndex: Map<number, number>
}

const LEGACY_FOLIATION_REGEX_SOURCE = String.raw`(?:fol|p|pp|pdf)(?:[\u002e\u200f\u200e\u202a\u202b\u202c\u202d\u202e]|\s)*\d+[rv]?`
const FOLIATION_REGEX = new RegExp(
  `(${LEGACY_FOLIATION_REGEX_SOURCE}|${LO_MARKER_REGEX_SOURCE}|${HIDDEN_EDITORIAL_MARKER_REGEX_SOURCE})`,
  'gi'
)

export const extractWordSpans = (text: string): WordSpan[] => {
  const spans: WordSpan[] = []
  const matcher = /\S+/g
  let match: RegExpExecArray | null = matcher.exec(text)
  while (match) {
    spans.push({
      word: match[0],
      start: match.index,
      end: match.index + match[0].length
    })
    match = matcher.exec(text)
  }
  return spans
}

export const extractFoliationSpans = (text: string, wordSpans: WordSpan[]): FoliationSpan[] => {
  if (!text || wordSpans.length === 0) {
    return []
  }

  const markers: FoliationSpan[] = []
  const matcher = new RegExp(FOLIATION_REGEX.source, 'gi')
  let match: RegExpExecArray | null = matcher.exec(text)

  while (match) {
    const rawMarker = match[0] || ''
    const marker = rawMarker.trim()
    const start = match.index
    const end = start + rawMarker.length

    if (marker) {
      let startWordIndex = -1
      let endWordIndex = -1

      for (let index = 0; index < wordSpans.length; index += 1) {
        const span = wordSpans[index]
        const overlaps = span.end > start && span.start < end
        if (!overlaps) {
          continue
        }
        if (startWordIndex === -1) {
          startWordIndex = index
        }
        endWordIndex = index
      }

      if (startWordIndex >= 0 && endWordIndex >= 0) {
        markers.push({
          marker,
          start,
          end,
          startWordIndex,
          endWordIndex,
          showInUi: !shouldHideMarkerInWorkspace(marker)
        })
      }
    }

    match = matcher.exec(text)
  }

  return markers
}

export const getSegmentableWordData = (wordSpans: WordSpan[], foliationSpans: FoliationSpan[]): SegmentableWordData => {
  const excludedWordIndices = new Set<number>()
  foliationSpans.forEach(marker => {
    for (let index = marker.startWordIndex; index <= marker.endWordIndex; index += 1) {
      excludedWordIndices.add(index)
    }
  })

  const segmentableWordSpans: WordSpan[] = []
  const originalToSegmentableWordIndex = new Map<number, number>()

  wordSpans.forEach((span, originalIndex) => {
    if (excludedWordIndices.has(originalIndex)) {
      return
    }
    const segmentableIndex = segmentableWordSpans.length
    segmentableWordSpans.push(span)
    originalToSegmentableWordIndex.set(originalIndex, segmentableIndex)
  })

  return {
    segmentableWordSpans,
    originalToSegmentableWordIndex
  }
}

export const stripFoliationMarkers = (text: string): string => {
  if (!text) {
    return text
  }
  return text.replace(new RegExp(FOLIATION_REGEX.source, 'gi'), '').trim()
}

export const extractDistinctFoliationMarkers = (text: string): string[] => {
  if (!text) {
    return []
  }
  const matcher = new RegExp(FOLIATION_REGEX.source, 'gi')
  const markers: string[] = []
  const seen = new Set<string>()
  let match: RegExpExecArray | null = matcher.exec(text)

  while (match) {
    const marker = (match[0] || '').trim()
    if (marker && !shouldHideMarkerInWorkspace(marker)) {
      const normalized = marker.toLowerCase()
      if (!seen.has(normalized)) {
        seen.add(normalized)
        markers.push(marker)
      }
    }
    match = matcher.exec(text)
  }

  return markers
}

export const normalizeSplitIndices = (splitIndices: number[], maxWordCount: number): number[] => {
  if (maxWordCount <= 1) {
    return []
  }
  const sorted = splitIndices
    .map(index => Math.floor(index))
    .filter(index => index > 0 && index < maxWordCount)
    .sort((left, right) => left - right)

  const deduped: number[] = []
  sorted.forEach(index => {
    if (deduped[deduped.length - 1] !== index) {
      deduped.push(index)
    }
  })
  return deduped
}

const normalizeIdRemap = (value: unknown): Record<string, string[]> => {
  if (!value || typeof value !== 'object') {
    return {}
  }

  const normalized: Record<string, string[]> = {}
  Object.entries(value as Record<string, unknown>).forEach(([key, targets]) => {
    const source = String(key || '').trim()
    if (!source || !Array.isArray(targets)) {
      return
    }
    const dedupedTargets = Array.from(new Set(
      targets
        .map(target => String(target || '').trim())
        .filter(target => target.length > 0)
    ))
    if (dedupedTargets.length > 0) {
      normalized[source] = dedupedTargets
    }
  })
  return normalized
}

const spanOverlapLength = (
  leftStart: number,
  leftEnd: number,
  rightStart: number,
  rightEnd: number
): number => {
  const overlapStart = Math.max(leftStart, rightStart)
  const overlapEnd = Math.min(leftEnd, rightEnd)
  return Math.max(0, overlapEnd - overlapStart)
}

interface PreviousUnitReferences {
  start: number
  end: number
  references: string[]
}

const extractPreviousUnitReferences = (
  previousSegmentationResult?: CompleteMicroSegmentationResult | null
): PreviousUnitReferences[] => {
  if (!previousSegmentationResult || !Array.isArray(previousSegmentationResult.chunk_results)) {
    return []
  }

  const referenceUnits: PreviousUnitReferences[] = []
  previousSegmentationResult.chunk_results.forEach(chunk => {
    const microUnits: MicroUnit[] = Array.isArray(chunk?.micro_units) ? chunk.micro_units as MicroUnit[] : []
    microUnits.forEach(unit => {
      const start = typeof unit?.start_position === 'number' ? unit.start_position : null
      const end = typeof unit?.end_position === 'number' ? unit.end_position : null
      if (start === null || end === null || end <= start) {
        return
      }

      const references = [unit.unit_uuid, unit.unit_id, unit.unit_label]
        .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
        .map(value => value.trim())
      if (references.length === 0) {
        return
      }

      referenceUnits.push({
        start,
        end,
        references: Array.from(new Set(references))
      })
    })
  })

  return referenceUnits
}

const buildMergedIdRemap = ({
  previousSegmentationResult,
  segmentRanges,
  stableUnitIds
}: {
  previousSegmentationResult?: CompleteMicroSegmentationResult | null
  segmentRanges: Array<{ start: number; end: number; text: string }>
  stableUnitIds: string[]
}): Record<string, string[]> => {
  const previousReferenceUnits = extractPreviousUnitReferences(previousSegmentationResult)
  const mergedIdRemap = normalizeIdRemap(previousSegmentationResult?.id_remap)
  if (previousReferenceUnits.length === 0 || segmentRanges.length === 0 || stableUnitIds.length === 0) {
    return mergedIdRemap
  }

  previousReferenceUnits.forEach(previousUnit => {
    let bestNextIndex = -1
    let bestOverlap = 0

    segmentRanges.forEach((range, index) => {
      const overlap = spanOverlapLength(previousUnit.start, previousUnit.end, range.start, range.end)
      if (overlap <= 0) {
        return
      }
      if (overlap > bestOverlap) {
        bestOverlap = overlap
        bestNextIndex = index
      }
    })

    if (bestNextIndex < 0) {
      return
    }

    const remappedTo = stableUnitIds[bestNextIndex]
    if (!remappedTo) {
      return
    }

    previousUnit.references.forEach(reference => {
      if (!reference || reference === remappedTo) {
        return
      }
      const existingTargets = Array.isArray(mergedIdRemap[reference])
        ? mergedIdRemap[reference]
        : []
      if (existingTargets.includes(remappedTo)) {
        return
      }
      mergedIdRemap[reference] = [...existingTargets, remappedTo]
    })
  })

  return mergedIdRemap
}

const createManualUnitUuid = (chunkId: string, unitIndex: number): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  const timestamp = Date.now().toString(36)
  const entropy = Math.random().toString(36).slice(2, 10)
  return `${chunkId}_uuid_${unitIndex + 1}_${timestamp}_${entropy}`
}

export const buildManualSegmentationResult = ({
  text,
  witnessId,
  witnessSigla,
  clusterId,
  sourceGroundTruthSigla,
  splitIndices,
  propagated,
  previousSegmentationResult
}: {
  text: string
  witnessId: string
  witnessSigla: string
  clusterId: string
  sourceGroundTruthSigla: string
  splitIndices: number[]
  propagated: boolean
  previousSegmentationResult?: CompleteMicroSegmentationResult | null
}): CompleteMicroSegmentationResult => {
  const chunkId = `manual_cluster_${clusterId}`
  const wordSpans = extractWordSpans(text)
  const foliationSpans = extractFoliationSpans(text, wordSpans)
  const { segmentableWordSpans } = getSegmentableWordData(wordSpans, foliationSpans)
  const normalizedSplits = normalizeSplitIndices(splitIndices, segmentableWordSpans.length)

  if (segmentableWordSpans.length === 0) {
    return {
      preprocessing_source: witnessId,
      segmentation_metadata: {
        timestamp: new Date().toISOString(),
        mode: 'manual_cluster_propagation',
        cluster_id: clusterId,
        source_ground_truth_sigla: sourceGroundTruthSigla,
        propagated,
        witness_sigla: witnessSigla,
        total_chunks: 0,
        successful_chunks: 0,
        failed_chunks: 0,
        manual_split_indices: [],
        foliation_ignored: true
      },
      chunk_results: [],
      aggregated_statistics: {
        total_units: 0,
        total_chunks: 0,
        avg_confidence: 0
      }
    }
  }

  const boundaries = [0, ...normalizedSplits, segmentableWordSpans.length]
  const segmentRanges: Array<{ start: number; end: number; text: string }> = []
  for (let idx = 0; idx < boundaries.length - 1; idx += 1) {
    const startWordIndex = boundaries[idx]
    const endWordExclusive = boundaries[idx + 1]
    const startSpan = segmentableWordSpans[startWordIndex]
    const endSpan = segmentableWordSpans[endWordExclusive - 1]
    if (!startSpan || !endSpan) {
      continue
    }
    const startPosition = startSpan.start
    const endPosition = endSpan.end
    if (endPosition <= startPosition) {
      continue
    }
    const unitText = stripFoliationMarkers(text.slice(startPosition, endPosition))
    if (!unitText) {
      continue
    }
    segmentRanges.push({
      start: startPosition,
      end: endPosition,
      text: unitText
    })
  }

  const previousIdentityUnits = extractIdentityUnitsFromSegmentationResult(previousSegmentationResult || null)
  const stableUnitIds = assignStableUnitIdsByOverlap({
    nextSpans: segmentRanges.map(range => ({ start: range.start, end: range.end })),
    previousSpans: previousIdentityUnits,
    createId: index => createManualUnitUuid(chunkId, index)
  })
  const mergedIdRemap = buildMergedIdRemap({
    previousSegmentationResult,
    segmentRanges,
    stableUnitIds
  })
  const microUnits: MicroUnit[] = []
  segmentRanges.forEach((range, idx) => {
    microUnits.push({
      unit_id: `${chunkId}_U${idx + 1}`,
      unit_uuid: stableUnitIds[idx],
      unit_number: idx + 1,
      source_chunk_id: chunkId,
      parent_primary_chunk: 'P0',
      unit_type: 'manual_cluster',
      unit_description: 'Manual cluster propagation unit',
      start_position: range.start,
      end_position: range.end,
      chunk_relative_start: range.start,
      chunk_relative_end: range.end,
      unit_length_chars: range.end - range.start,
      unit_text_content: range.text,
      confidence_score: 1,
      unit_label: `C${clusterId}.${idx + 1}`,
      processing_metadata: {
        manual_cluster: true,
        cluster_id: clusterId,
        source_ground_truth_sigla: sourceGroundTruthSigla,
        propagated
      }
    })
  })

  const chunkResult: ChunkProcessingResult = {
    source_chunk_id: chunkId,
    parent_primary_chunk: 'P0',
    processing_time: 0,
    processing_status: 'success',
    fallback_used: false,
    micro_units: microUnits,
    chunk_position_data: {
      start_position: 0,
      end_position: text.length,
      relative_start: 0,
      relative_end: text.length
    }
  }

  return {
    preprocessing_source: witnessId,
    segmentation_metadata: {
      timestamp: new Date().toISOString(),
      mode: 'manual_cluster_propagation',
      cluster_id: clusterId,
      source_ground_truth_sigla: sourceGroundTruthSigla,
      propagated,
      witness_sigla: witnessSigla,
      total_chunks: 1,
      successful_chunks: 1,
      failed_chunks: 0,
      manual_split_indices: normalizedSplits,
      foliation_ignored: true
    },
    chunk_results: [chunkResult],
    aggregated_statistics: {
      total_units: microUnits.length,
      total_chunks: 1,
      avg_confidence: 1,
      pass_through_mode: false,
      propagation_mode: true
    },
    ...(Object.keys(mergedIdRemap).length > 0 ? { id_remap: mergedIdRemap } : {})
  }
}
